import { DateRange, ReconFilters } from "../types";
import { numOr } from "../helpers";

export interface EntryFilterState {
  filters: ReconFilters;
  dateRange: DateRange;
  query: string;
  uploadFilter: string | null;
}

export interface AdvancedCondition {
  field: string;
  operator: string;
  values: string[];
}

export interface SavedAdvancedField {
  name: string;
  operator: string;
  value: string;
  hasBetween: boolean;
  betweenValue?: string;
}

export function advancedConditions(
  fields: SavedAdvancedField[],
): AdvancedCondition[] {
  return fields.map((field) => ({
    field: field.name,
    operator: field.operator,
    values: [field.value, ...(field.hasBetween ? [field.betweenValue] : [])]
      .filter((value) => value != null)
      .map(String),
  }));
}

export interface EntryFiltersInput {
  advancedFields?: AdvancedCondition[];
  q?: string;
  from?: string;
  to?: string;
  latestRunOnly?: boolean;
  matchStatuses?: string[];
  amountMin?: number;
  amountMax?: number;
  priceMin?: number;
  priceMax?: number;
  uploadPublicId?: string;
  includeReversed?: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const isoDay = (date: Date): string => date.toISOString().split("T")[0];

function dateWindow(
  dateRange: DateRange,
  now: Date,
): Pick<EntryFiltersInput, "from" | "to" | "latestRunOnly"> {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  if (dateRange === "latest") return { latestRunOnly: true };
  if (dateRange === "today") return { from: isoDay(today), to: isoDay(today) };
  if (dateRange === "7d")
    return {
      from: isoDay(new Date(today.getTime() - 7 * DAY_MS)),
      to: isoDay(today),
    };
  if (dateRange === "30d")
    return {
      from: isoDay(new Date(today.getTime() - 30 * DAY_MS)),
      to: isoDay(today),
    };
  return {};
}

const finite = (value: string): number | undefined => {
  const parsed = numOr(value, NaN);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export function entryFilters(
  { filters, dateRange, query, uploadFilter }: EntryFilterState,
  now: Date = new Date(),
): EntryFiltersInput {
  const input: EntryFiltersInput = {
    ...dateWindow(dateRange, now),
    q: query.trim() || undefined,
    matchStatuses: filters.matches.length ? filters.matches : undefined,
    amountMin: finite(filters.amountMin),
    amountMax: finite(filters.amountMax),
    priceMin: finite(filters.priceMin),
    priceMax: finite(filters.priceMax),
    uploadPublicId: uploadFilter || undefined,
    includeReversed: filters.includeReversed || undefined,
  };
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  ) as EntryFiltersInput;
}
